import React, { useMemo, useState } from 'react';
import {
    Typography, Box, Container, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, FormControl, InputLabel, Select, Checkbox, ListItemText, OutlinedInput,
    SelectChangeEvent, IconButton
} from '@mui/material';
import { AddCircle, RemoveCircle } from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import {
    fetchServices,
    fetchAllEmployees,
    addService,
    deleteService,
    updateService,
    getBusinessConfigById
} from '../../firebase/firebase-delegate';
import { BusinessConfig, Employee, Service } from '../../types';
import { TIME_INTERVAL } from '../../constants';
import { formatMinutes } from '../../time/timeUtils';
import EditableServicesSection from '../../components/employee/services/EditableServicesSection';
import SuccessOrErrorDialog from '../../components/global/SuccessOrErrorDialog';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import ImageSelect from '../../components/global/images/ImageSelector';
import { resolveBookingUxConfig } from '../../utils/bookingUxConfig';

const SuperAdminEditServicesPage: React.FC = () => {
    const { siteId } = useParams();
    const [isAddServiceDialogOpen, setIsAddServiceDialogOpen] = useState(false);
    const [resultDialogOpen, setResultDialogOpen] = useState(false);
    const [addedServiceImagePlaceholder, setAddedServiceImagePlaceholder] = useState<File | undefined>(undefined);
    const [addedServicePlaceholder, setAddedServicePlaceholder] = useState<Omit<Service, 'id'>>({
        title: '',
        description: '',
        price: 0,
        time: 0,
        employees: []
    });

    const servicesQuery = useQuery<Service[]>({
        queryKey: ['services', siteId],
        queryFn: () => fetchServices(siteId),
        enabled: !!siteId
    });

    const employeesQuery = useQuery<Employee[]>({
        queryKey: ['allEmployees', siteId],
        queryFn: () => fetchAllEmployees(siteId),
        enabled: !!siteId
    });

    const businessConfigQuery = useQuery<BusinessConfig | null>({
        queryKey: ['businessConfig', siteId],
        enabled: !!siteId,
        queryFn: () => getBusinessConfigById(siteId!)
    });
    const bookingUxConfig = resolveBookingUxConfig(businessConfigQuery.data);
    const resourcePlural = bookingUxConfig.terminology.resourcePlural;
    const isResourceMode = bookingUxConfig.bookingModel === 'resource';
    const ownerId = businessConfigQuery.data?.paymentInfo?.owner?.trim() || '';
    const selectableEmployees = useMemo(() => {
        const allEmployees = employeesQuery.data ?? [];
        if (!isResourceMode || !ownerId) return allEmployees;
        return allEmployees.filter((employee) => employee.id !== ownerId);
    }, [employeesQuery.data, isResourceMode, ownerId]);

    const addServiceMutation = useMutation({
        mutationFn: async () => {
            await addService(addedServicePlaceholder, addedServiceImagePlaceholder, siteId);
        },
        onSuccess: () => {
            servicesQuery.refetch();
        },
        onSettled: () => {
            resetAddServiceDialog();
            setResultDialogOpen(true);
        }
    });

    const updateServiceMutation = useMutation({
        mutationFn: async ({ oldService, updatedService, image }: { oldService: Service, updatedService: Service, image?: File }) =>
            await updateService(oldService, updatedService, image, siteId),
        onSuccess: () => {
            servicesQuery.refetch();
        },
        onSettled: () => setResultDialogOpen(true)
    });

    const deleteServiceMutation = useMutation({
        mutationFn: async (service: Service) => await deleteService(service, siteId),
        onSuccess: () => {
            servicesQuery.refetch();
        },
        onSettled: () => setResultDialogOpen(true)
    });

    const handleEmployeeChange = (event: SelectChangeEvent<string[]>) => {
        const {
            target: { value },
        } = event;
        setAddedServicePlaceholder((prev) => ({
            ...prev,
            employees: typeof value === 'string' ? value.split(',') : value,
        }));
    };

    const handleDurationChange = (increment: boolean) => {
        setAddedServicePlaceholder((prev) => ({
            ...prev,
            time: increment ? prev.time + TIME_INTERVAL : Math.max(prev.time - TIME_INTERVAL, 0),
        }));
    };

    const validateFields = () => {
        const { title, price, employees, time } = addedServicePlaceholder;
        return title.trim() !== '' && price > 0 && employees.length > 0 && time > 0;
    };

    const handleAddService = () => {
        if (validateFields()) {
            addServiceMutation.mutate();
        } else {
            alert('Please fill out all required fields.');
        }
    };

    const resetAddServiceDialog = () => {
        setAddedServicePlaceholder({ title: '', description: '', price: 0, time: 0, employees: [] });
        setAddedServiceImagePlaceholder(undefined);
        setIsAddServiceDialogOpen(false);
    };

    if (!siteId) return <Typography color="error">Missing site ID</Typography>;
    if (servicesQuery.isLoading || employeesQuery.isLoading) return <LoadingSpinner />;
    if (servicesQuery.isError || employeesQuery.isError) return <Typography color="error">Failed to load data.</Typography>;

    const ITEM_HEIGHT = 48;
    const ITEM_PADDING_TOP = 8;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                width: 250,
            },
        },
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4 }}>
            <Typography variant="h5" fontWeight="bold" align="center" gutterBottom>
                Manage Services for {businessConfigQuery.data?.businessInfo.name}
            </Typography>

            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => setIsAddServiceDialogOpen(true)}
                >
                    Add Service
                </Button>
            </Box>

            <hr style={{ border: 'none', height: '2px', backgroundColor: 'rgba(0,0,0,0.2)', marginBottom: '20px' }} />

            <EditableServicesSection
                siteId={siteId}
                services={servicesQuery.data || []}
                employees={selectableEmployees}
                onSaveService={(oldService, updatedService, image) =>
                    updateServiceMutation.mutate({ oldService, updatedService, image })}
                onDeleteService={(service) => deleteServiceMutation.mutate(service)}
                resourcePluralLabel={resourcePlural}
            />

            <Dialog open={isAddServiceDialogOpen} onClose={resetAddServiceDialog}>
                <DialogTitle sx={{ textAlign: 'center' }}>Add New Service</DialogTitle>
                <DialogContent>
                    <Box mb={2}>
                        <ImageSelect
                            initialImagePath={undefined}
                            onImageChange={(file) => setAddedServiceImagePlaceholder(file)}
                            enabled={true}
                            altText="Service Image"
                        />
                    </Box>
                    <TextField
                        label="Title"
                        fullWidth
                        margin="dense"
                        value={addedServicePlaceholder.title}
                        onChange={(e) => setAddedServicePlaceholder({ ...addedServicePlaceholder, title: e.target.value })}
                    />
                    <TextField
                        label="Description"
                        fullWidth
                        margin="dense"
                        value={addedServicePlaceholder.description}
                        onChange={(e) => setAddedServicePlaceholder({ ...addedServicePlaceholder, description: e.target.value })}
                    />
                    <TextField
                        label="Price"
                        fullWidth
                        margin="dense"
                        type="number"
                        value={addedServicePlaceholder.price}
                        onChange={(e) => setAddedServicePlaceholder({ ...addedServicePlaceholder, price: parseFloat(e.target.value) || 0 })}
                    />
                    <Box display="flex" alignItems="center" my={2}>
                        <IconButton onClick={() => handleDurationChange(false)}><RemoveCircle /></IconButton>
                        <TextField
                            label="Duration"
                            value={formatMinutes(addedServicePlaceholder.time)}
                            inputProps={{ readOnly: true }}
                            fullWidth
                        />
                        <IconButton onClick={() => handleDurationChange(true)}><AddCircle /></IconButton>
                    </Box>
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="employees-label">{resourcePlural}</InputLabel>
                        <Select
                            labelId="employees-label"
                            multiple
                            value={addedServicePlaceholder.employees}
                            onChange={handleEmployeeChange}
                            input={<OutlinedInput label={resourcePlural} />}
                            renderValue={(selected) =>
                                selected.map(id =>
                                    (() => {
                                      const employee = selectableEmployees.find((emp) => emp.id === id);
                                      return employee ? `${employee.firstName} ${employee.lastName}`.trim() : '';
                                    })()
                                ).filter(Boolean).join(', ')
                            }
                            MenuProps={MenuProps}
                        >
                            {selectableEmployees.map((employee) => (
                                <MenuItem key={employee.id} value={employee.id}>
                                    <Checkbox checked={addedServicePlaceholder.employees.includes(employee.id)} />
                                    <ListItemText primary={`${employee.firstName} ${employee.lastName}`.trim()} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={resetAddServiceDialog}>Cancel</Button>
                    <Button onClick={handleAddService} color="primary">Add</Button>
                </DialogActions>
            </Dialog>

            <SuccessOrErrorDialog
                open={resultDialogOpen}
                onClose={() => setResultDialogOpen(false)}
                success={addServiceMutation.isSuccess || updateServiceMutation.isSuccess || deleteServiceMutation.isSuccess}
                successTitle="Success"
                successMessage="Service changes saved successfully."
                errorTitle="Error"
                errorMessage="Something went wrong. Please try again."
            />
        </Container>
    );
};

export default SuperAdminEditServicesPage;
